export const formBlockDocumentationMarkdown = `
### What does this block do?

The Form Block allows healthcare administrators to deliver customized patient forms as part of a workflow. It sends one or more form templates to a patient and collects form answers. The block can be configured with dynamic parameters to personalize parts of the form, and scheduling and auto-export options control post-form completion behavior.

### When to use it?

- Use this block when you need to deliver patient-facing forms for data capture, such as pre-visit questionnaires, consent forms, or follow-up surveys.  
- Employ it in workflows where forms must be dynamically tailored with parameters (e.g., pre-filled patient info or appointment details).  
- Integrate this block when scheduled or automatic export of form responses is required for further processing (such as integration with EHR systems).  
- Use it in test scenarios where you want to run the flow without actually deploying the form set.

### Example Flows

1. **Pre-Visit Survey Distribution:**  
   After scheduling an appointment, the flow triggers the Form Block to send a pre-visit survey. Dynamic parameters are used to insert appointment time and patient name. On form completion, auto-export functionality sends the responses to a data repository.

2. **Consent and Compliance Collection:**  
   In a consent workflow, the Form Block delivers legal or consent forms to a patient. Multiple form templates can be attached as needed. Once the patient completes the form, the responses are automatically exported for compliance documentation.

3. **Follow-Up Feedback Request:**  
   Post-discharge, the block is used to send a survey gathering feedback on the patient's experience. Dynamic key/value parameters customize the survey based on the patient's treatment details, and a refresh interval can ensure that the same form isn't resent too frequently.

## How to Configure This Block

### **1. Form Selection**  
- **Description:** In the Configuration tab, locate the Form header with its purple user/profile icon.  
- **Steps:**  
   1. In the "Add form" section, use the search field ("Search from existing forms") to find and select form templates.  
   2. Helper text reminds you: "Select from existing forms or create a new form" – clicking the link opens the form creation interface at:  
      [Form Template Builder](https://app.notablehealth.com/staff/app#/previsit-form-templates).

### **2. Dynamic Parameters**  
- **Description:** Below the form selection, there is a table labeled "Dynamic Parameters" with "Key" and "Value" columns.  
- **Steps:**  
   1. In the first row, you will see input fields labeled "Add key" and "Add value" along with a delete button.  
   2. Use the "+ Add row" button (in blue at the bottom right) to add more parameter rows as needed.  
   3. These parameters allow you to personalize the form content based on patient-specific data.

### **3. Configuration Options**  

#### **Scheduling Configurations:**  
- Toggle this option (default Off) if you need to tie form delivery to specific scheduling rules.

#### **Run Auto Export:**  
- Toggle this option to On if you want the form responses to automatically trigger export actions such as exporting forms and patient information after form submission.

#### **Test Mode:**  
- Toggle this option to Off (default) so that the flow executes the actual form distribution process.  
- Enabling Test Mode will run the flow without executing the associated form set.

#### **Mock Output for Test Run:**  
- At the bottom of the configuration form, toggle the "Mock output for test run" switch.  
- When enabled, the block skips real execution and returns preset data, allowing you to test downstream steps without sending real forms.

### **4. Save Your Configuration**  
Once you have completed the selections and input all required dynamic parameters and options, save your configuration to update the flow.

### **5. Form Template Configuration (per template)**  

#### **Form Template ID:**  
- This is a unique identifier for the selected template.

#### **Refresh Interval (optional):**  
- Specifies how frequently the same form can be re-attached for the patient after the last completion.  
- Enter a positive integer and select from available duration units (minutes, hours, days).

## How to Test

### **1. Enable Test Mode or Mock Output**  
- Toggle "Test Mode" to run the flow without sending the actual form set, or enable "Mock output for test run" to simulate the execution.

### **2. Execute a Test Flow**  
- Run your flow with test patient data to ensure the selected form(s) are correctly attached and that dynamic parameters are replaced accurately.

### **3. Review Export Actions**  
- If Run Auto Export is enabled, verify that the form responses trigger the appropriate export actions.

### **4. Validate UI Behavior**  
- Ensure that any errors (e.g., missing form selection) appear as expected and that the dynamic parameters table functions correctly (e.g., adding/removing rows).

## Best Practices

1. **Select Valid Form Templates:**  
   Use the provided search field to select forms that are well-tested and contain all required fields.

2. **Use Dynamic Parameters Effectively:**  
   Map keys and values clearly to ensure that patient-specific or context-specific data is accurately inserted into the forms.

3. **Test Thoroughly:**  
   Always test your form configuration with mock output before moving to a live patient scenario.

4. **Monitor Refresh Intervals:**  
   Configure refresh intervals thoughtfully to avoid resending the same form too frequently.

5. **Document Configuration:**  
   Keep records of which forms and dynamic parameters are used, so that troubleshooting and later updates are straightforward.

### Example Output

~~~json
{
   "formSet": {
      "id": "00000000-0000-0000-0000-000000000000",
      "status": "CREATED",
      "created": "2025-03-27T16:57:10.502Z",
      "expires": "2025-03-30T16:57:10.502Z",
      "updated": "2025-03-27T16:57:10.502Z",
      "encounterId": null,
      "authBypassCode": null,
      "previsitLinkId": null,
      "authCodeExpires": "2025-03-30T16:57:10.502Z",
      "closedTimestamp": null,
      "ehrInformationId": "0195d373-c6d4-78cd-bae4-aa5d33906958",
      "authenticationCode": "00000000",
      "currentStateExported": false
   }
}
~~`;
